import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { createOtpCooldowns, cloneOtpCooldowns, OtpChannel, OtpCooldowns } from '@/shared/lib/otp/cooldowns';

interface RecoverySession {
  recoveryToken: string;
  sendChannel: 'max' | 'sms' | null;
  cooldowns: OtpCooldowns;
  remainingAttempts: number | null;
  lockedUntil: number | null;
  lockoutMessage: string | null;
}

interface AppState {
  phoneNumber: string;
  sendCodeAt: number | null;
  recoveryToken: string;
  maxDeepLink: string | null;
  cooldowns: OtpCooldowns;
  lastResendChannel: 'max' | 'sms' | null;
  sendChannel: 'max' | 'sms' | null;
  remainingAttempts: number | null;
  lockedUntil: number | null;
  lockoutMessage: string | null;
  sessions: Record<string, RecoverySession>;
}

const initialState: AppState = {
  phoneNumber: '',
  sendCodeAt: null,
  recoveryToken: '',
  maxDeepLink: null,
  cooldowns: createOtpCooldowns(),
  lastResendChannel: null,
  sendChannel: null,
  remainingAttempts: null,
  lockedUntil: null,
  lockoutMessage: null,
  sessions: {},
};

interface ResendCooldownPayload {
  channel: OtpChannel;
  timestamp: number;
  seconds: number;
}

export const recoveryFormSlice = createSlice({
  name: 'recoveryForm',
  initialState,
  reducers: {
    setPhoneNumber: (state, action: PayloadAction<string>) => {
      state.phoneNumber = action.payload;
    },
    setSendCodeAt: (state, action: PayloadAction<number>) => {
      state.sendCodeAt = action.payload;
    },
    setRecoveryToken: (state, action: PayloadAction<string>) => {
      state.recoveryToken = action.payload;
    },
    setMaxDeepLink: (state, action: PayloadAction<string | null>) => {
      state.maxDeepLink = action.payload;
    },
    setResendCooldown: (state, action: PayloadAction<ResendCooldownPayload>) => {
      state.cooldowns[action.payload.channel] = {
        timestamp: action.payload.timestamp,
        seconds: action.payload.seconds,
      };
    },
    setLastResendChannel: (state, action: PayloadAction<'max' | 'sms'>) => {
      state.lastResendChannel = action.payload;
    },
    setSendChannel: (state, action: PayloadAction<'max' | 'sms'>) => {
      state.sendChannel = action.payload;
    },
    setRemainingAttempts: (state, action: PayloadAction<number | null>) => {
      state.remainingAttempts = action.payload;
    },
    setLockedUntil: (state, action: PayloadAction<number | null>) => {
      state.lockedUntil = action.payload;
    },
    setLockoutMessage: (state, action: PayloadAction<string | null>) => {
      state.lockoutMessage = action.payload;
    },
    saveRecoverySession: (state, action: PayloadAction<string>) => {
      state.sessions[action.payload] = {
        recoveryToken: state.recoveryToken,
        sendChannel: state.sendChannel,
        cooldowns: cloneOtpCooldowns(state.cooldowns),
        remainingAttempts: state.remainingAttempts,
        lockedUntil: state.lockedUntil,
        lockoutMessage: state.lockoutMessage,
      };
    },
    restoreRecoverySession: (state, action: PayloadAction<string>) => {
      const session = state.sessions[action.payload];
      if (!session) return;
      state.recoveryToken = session.recoveryToken;
      state.sendChannel = session.sendChannel;
      state.cooldowns = cloneOtpCooldowns(session.cooldowns);
      state.remainingAttempts = session.remainingAttempts;
      state.lockedUntil = session.lockedUntil;
      state.lockoutMessage = session.lockoutMessage;
    },
    resetRecoveryCooldowns: (state) => {
      state.cooldowns = createOtpCooldowns();
    },
    resetRecoverySession: (state) => {
      state.sendCodeAt = null;
      state.recoveryToken = '';
      state.maxDeepLink = null;
      state.cooldowns = createOtpCooldowns();
      state.lastResendChannel = null;
      state.sendChannel = null;
      state.remainingAttempts = null;
      state.lockedUntil = null;
      state.lockoutMessage = null;
    },
    resetRecoveryData: () => initialState,
  },
});

export const {
  setPhoneNumber,
  setSendCodeAt,
  setRecoveryToken,
  setMaxDeepLink,
  setResendCooldown,
  setLastResendChannel,
  setSendChannel,
  setRemainingAttempts,
  setLockedUntil,
  setLockoutMessage,
  resetRecoveryCooldowns,
  resetRecoverySession,
  resetRecoveryData,
  saveRecoverySession,
  restoreRecoverySession,
} = recoveryFormSlice.actions;

export const { reducer: recoveryFormReducer } = recoveryFormSlice;
